import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Edit2, Trash2, X, Crown, Check } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";

const blank = {
  name: "",
  tagline: "",
  price: 9.90,
  period: "month",
  features: [""],
  popular: false,
  active: true,
  order: 0,
  color: "purple",
};

export default function AdminPlans() {
  const [plans, setPlans] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/plans");
      setPlans(r.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await api.get("/admin/plans");
        if (mounted) setPlans(r.data);
      } catch (e) { console.error(e); }
    })();
    return () => { mounted = false; };
  }, []);

  const onDelete = async (p) => {
    if (!window.confirm(`Delete plan "${p.name}"?`)) return;
    await api.delete(`/admin/plans/${p.id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="space-y-6" data-testid="admin-plans">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-white">Premium Plans</h1>
          <p className="text-sm text-slate-400 mt-1">Manage GAMEFLOW Premium membership tiers.</p>
        </div>
        <Button onClick={() => { setEditing({ ...blank }); setOpen(true); }} className="bg-[#A855F7] hover:bg-[#9333EA]" data-testid="new-plan-btn">
          <Plus className="w-4 h-4 mr-1.5" /> New Plan
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {plans.map((p) => (
          <div key={p.id} className={`relative rounded-2xl p-6 ${p.popular ? "bg-gradient-to-br from-[#A855F7]/20 to-[#7E22CE]/20 border-2 border-[#A855F7]/40" : "bg-[#171723] border border-white/5"}`}>
            {p.popular && <div className="absolute -top-2.5 right-4 px-2.5 py-0.5 rounded-full bg-[#A855F7] text-white text-[10px] font-bold uppercase">Popular</div>}
            <div className="flex items-center justify-between">
              <h3 className="font-display text-lg font-bold text-white flex items-center gap-2">{p.name} {!p.active && <span className="text-[10px] px-1.5 rounded bg-red-500/20 text-red-300">paused</span>}</h3>
              <Crown className={`w-5 h-5 ${p.color === "gold" ? "text-[#FBBF24]" : p.color === "purple" ? "text-[#A855F7]" : "text-[#C084FC]"}`} />
            </div>
            <p className="text-xs text-slate-400 mt-1">{p.tagline}</p>
            <div className="mt-3"><span className="font-display text-2xl font-bold text-white">${p.price}</span><span className="text-xs text-slate-400 ml-1">/{p.period}</span></div>
            <ul className="mt-3 space-y-1">
              {(p.features || []).slice(0, 4).map((f, i) => (
                <li key={i} className="text-xs text-slate-300 flex items-start gap-1.5"><Check className="w-3.5 h-3.5 text-[#C084FC] mt-0.5 shrink-0" />{f}</li>
              ))}
              {p.features?.length > 4 && <li className="text-xs text-slate-500">+ {p.features.length - 4} more…</li>}
            </ul>
            <div className="mt-4 flex gap-2">
              <Button size="sm" variant="outline" onClick={() => { setEditing({ ...p }); setOpen(true); }} className="flex-1 border-white/10 text-slate-200 hover:bg-white/5" data-testid={`edit-plan-${p.id}`}><Edit2 className="w-3.5 h-3.5 mr-1.5" />Edit</Button>
              <Button size="sm" variant="outline" onClick={() => onDelete(p)} className="border-white/10 text-slate-300 hover:bg-red-500/10 hover:text-red-400 hover:border-red-500/30" data-testid={`del-plan-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          </div>
        ))}
        {plans.length === 0 && <div className="md:col-span-3 rounded-2xl bg-[#171723] border border-white/5 p-10 text-center text-slate-500">No plans yet — create your first one.</div>}
      </div>

      <AnimatePresence>
        {open && editing && (
          <PlanModal plan={editing} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />
        )}
      </AnimatePresence>
    </div>
  );
}

function PlanModal({ plan, onClose, onSaved }) {
  const [form, setForm] = useState(plan);
  const isNew = !plan.id;

  const save = async () => {
    const payload = {
      ...form,
      price: parseFloat(form.price) || 0,
      order: parseInt(form.order) || 0,
      features: (form.features || []).filter((f) => f && f.trim()),
    };
    try {
      if (isNew) await api.post("/admin/plans", payload);
      else await api.put(`/admin/plans/${form.id}`, payload);
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setFeat = (i, v) => {
    const next = [...(form.features || [])];
    next[i] = v;
    setForm({ ...form, features: next });
  };
  const addFeat = () => setForm({ ...form, features: [...(form.features || []), ""] });
  const rmFeat = (i) => setForm({ ...form, features: form.features.filter((_, k) => k !== i) });

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur p-4" onClick={onClose}>
      <motion.div initial={{ scale: 0.96, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.96, opacity: 0 }} className="w-full max-w-2xl rounded-3xl bg-[#171723] border border-white/10 p-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="plan-modal">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display text-xl font-bold text-white">{isNew ? "New Plan" : "Edit Plan"}</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/5 text-slate-400"><X className="w-4 h-4" /></button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
          <Input label="Tagline" value={form.tagline} onChange={(v) => setForm({ ...form, tagline: v })} />
          <Input label="Price (USD)" type="number" value={form.price} onChange={(v) => setForm({ ...form, price: v })} />
          <Select label="Period" value={form.period} onChange={(v) => setForm({ ...form, period: v })} options={["month", "year", "lifetime"]} />
          <Select label="Color" value={form.color} onChange={(v) => setForm({ ...form, color: v })} options={["purple", "violet", "gold"]} />
          <Input label="Order" type="number" value={form.order} onChange={(v) => setForm({ ...form, order: v })} />
        </div>
        <div className="mt-4">
          <div className="text-xs text-slate-400 uppercase font-semibold mb-2">Features</div>
          <div className="space-y-2">
            {(form.features || []).map((f, i) => (
              <div key={i} className="flex gap-2">
                <input value={f} onChange={(e) => setFeat(i, e.target.value)} className="flex-1 h-9 px-3 rounded-lg bg-white/5 border border-white/10 text-sm text-white" placeholder="Feature description" />
                <button onClick={() => rmFeat(i)} className="px-2 rounded-lg hover:bg-red-500/10 text-slate-400 hover:text-red-400"><X className="w-4 h-4" /></button>
              </div>
            ))}
            <button onClick={addFeat} className="text-xs text-[#A855F7] hover:underline flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Add feature</button>
          </div>
        </div>
        <div className="mt-4 flex gap-5">
          <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={form.popular} onChange={(e) => setForm({ ...form, popular: e.target.checked })} className="accent-[#A855F7]" /> Mark as popular</label>
          <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-[#A855F7]" /> Active</label>
        </div>
        <div className="flex gap-3 mt-6">
          <Button onClick={save} className="bg-[#A855F7] hover:bg-[#9333EA] flex-1" data-testid="save-plan">{isNew ? "Create plan" : "Save changes"}</Button>
          <Button variant="outline" onClick={onClose} className="border-white/10 text-slate-300 hover:bg-white/5">Cancel</Button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function Input({ label, value, onChange, type = "text" }) {
  return (
    <label className="block">
      <div className="text-xs text-slate-400 uppercase font-semibold mb-1">{label}</div>
      <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full h-10 px-3 rounded-lg bg-white/5 border border-white/10 text-sm text-white focus:border-[#A855F7] focus:outline-none" />
    </label>
  );
}
function Select({ label, value, onChange, options }) {
  return (
    <label className="block">
      <div className="text-xs text-slate-400 uppercase font-semibold mb-1">{label}</div>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full h-10 px-3 rounded-lg bg-white/5 border border-white/10 text-sm text-white focus:border-[#A855F7] focus:outline-none">
        {options.map((o) => <option key={o} value={o} className="bg-[#171723]">{o}</option>)}
      </select>
    </label>
  );
}
